import {useContext, useEffect} from 'react'
import PropTypes from 'prop-types'

import {useNavigate, useSearchParams} from 'react-router-dom'
import {NavContext} from '../../context/global_context'
import {PROMOTE_CHANGES, TRACER_PREVIEW, TRACER_PREVIEW_MGMT,} from '../../constants/permission'
import {HasPermission} from '../../utils/permission'
import {Col, Empty, Form, Row, Table, Typography} from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import InputText from '../common/InputText'
import Button from '../common/Button'
import LinkWithStatus from '../common/LinkWithStatus'
import {ProgresSpinner} from '../common/ProgresSpinner'
import EllipsisTooltip from '../common/EllipsisTooltip'
import {useVehicles} from '../../context/vehicle_context'
// import NoRecordsFound from '../common/NoRecordsFound'
import {useIntl} from 'react-intl'
import RecordsCount from '../common/RecordsCount'

dayjs.extend(customParseFormat)

const Action = (props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { setSelectedVehicle } = useVehicles()
  const vehicle = props.vehicle
  const blockers = (vehicle.blockers.slice() || []).filter(
    (b) => b.name !== 'graph_reset'
  )
  const vehicleIsBlocked = blockers.length > 0
  const messages = {
    wire_list: intl.formatMessage({ id: 'vehicleTable.importingWirelist' }),
    dtcs: intl.formatMessage({ id: 'vehicleTable.importingDTCs' }),
    modules: intl.formatMessage({ id: 'vehicleTable.importingModules' }),
    inlines: intl.formatMessage({ id: 'vehicleTable.importingInlines' }),
    category_connector: intl.formatMessage({ id: 'vehicleTable.importingConnectorCategories' }),
    harness_description: intl.formatMessage({ id: 'vehicleTable.importingHarnessDescriptions' }),
    sig_list: intl.formatMessage({ id: 'vehicleTable.importingCircuitList' }),
    device_connections: intl.formatMessage({ id: 'vehicleTable.importingDeviceConnections' }),
    generating_wires: intl.formatMessage({ id: 'vehicleTable.wireGenerationInProgress' }),
  }
  const state = blockers
    .map((b) => {
      return messages[b.name]
    })
    .join('. ')

  return (
    <>
      {vehicleIsBlocked && (
        <ProgresSpinner caption={state} className="blockers" size="small" />
      )}
      {!vehicleIsBlocked && (
        <LinkWithStatus
          onClickFunc={() => {
            setSelectedVehicle(vehicle)
            navigate(`/vehicles/${vehicle.vehicle_id}/importers`)
          }}
          className="ant-link"
          title={intl.formatMessage({ id: 'vehicleTable.browse' })}
          testId="browse-vehicle"
        />
      )}
    </>
  )
}

const VehiclesTable = (props) => {
  const intl = useIntl()
  const { getPaginatedData } = props

  const [form] = Form.useForm()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchQuery = searchParams.get('search') || ''

  const [state, dispatch] = useContext(NavContext)
  const { vehicles, pagination } = useVehicles()
  const { currentPage, totalCount, totalPages, nextPageLink, prevPageLink } =
    pagination
  const navigate = useNavigate()

  useEffect(() => {
    form.setFieldsValue({ search: searchQuery })
  }, [searchQuery, form])
  const onFinish = (values) => {
    setSearchParams(values.search ? { search: values.search } : {})
  }
  /* Vehicle List Table Data */
  const data = vehicles?.map((vehicle, index) => {
    // vehicle.blockers.push({name: 'dtcs'})
    return {
      key: index,
      vehicleName: vehicle.name,
      makeDescription: vehicle.make_description,
      modelDescription: vehicle.model_description,
      makeModelId: vehicle.make_model_id,
      phase: vehicle.phase,
      year: vehicle.year,
      lastImportedAt: vehicle.last_imported_at,
      action: <Action vehicle={vehicle} />,
    }
  })
  /* Vehicle List Table columns */
  const columns = [
    {
      title: intl.formatMessage({ id: 'vehicleTable.vehicleName' }),
      className: 'vehicle-name',
      dataIndex: 'vehicleName',
      sorter: (a, b) => a.vehicleName.length - b.vehicleName.length,
      ellipsis: { showTitle: false },
      align: 'left',
      width: '18%',
      render: (text) => <EllipsisTooltip className='ellipsis'>{text}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'vehicleTable.makeDescription' }),
      className: 'make-description',
      dataIndex: 'makeDescription',
      sorter: (a, b) =>
        (a.makeDescription || '').localeCompare(b.makeDescription || ''),
      ellipsis: { showTitle: false },
      align: 'left',
      width: '14%',
      render: (text) => <EllipsisTooltip className='ellipsis' >{text || '-'} </EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'vehicleTable.modelDescription' }),
      className: 'model-description',
      dataIndex: 'modelDescription',
      sorter: (a, b) =>
        (a.modelDescription || '').localeCompare(b.modelDescription || ''),
      ellipsis: { showTitle: false },
      align: 'left',
      width: '19%',
      render: (text) => <EllipsisTooltip className='ellipsis' >{text || '-'} </EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'vehicleTable.modelId' }),
      className: 'make-model-id',
      dataIndex: 'makeModelId',
      sorter: (a, b) => a.makeModelId - b.makeModelId,
      ellipsis: { showTitle: false },
      align: 'left',
      width: '10%',
      render: (text) => <EllipsisTooltip className='ellipsis'>{text}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'vehicleTable.phase' }),
      className: 'phase',
      dataIndex: 'phase',
      sorter: (a, b) => (a.phase || '').localeCompare(b.phase || ''),
      ellipsis: { showTitle: false },
      align: 'left',
      width: '8%',
      render: (text) => <EllipsisTooltip className='ellipsis' >{text || '-'} </EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'vehicleTable.year' }),
      className: 'year',
      dataIndex: 'year',
      sorter: (a, b) => (parseInt(a.year) || 0) - (parseInt(b.year) || 0),
      ellipsis: { showTitle: false },
      align: 'left',
      width: '6%',
        render: (text) => <EllipsisTooltip className='ellipsis' >{text || '-'} </EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'vehicleTable.lastImport' }),
      className: 'last-imported-at',
      dataIndex: 'lastImportedAt',
      sorter: (a, b) =>
        dayjs(a.lastImportedAt, 'DD-MM-YYYY hh:mm A').valueOf() -
        dayjs(b.lastImportedAt, 'DD-MM-YYYY hh:mm A').valueOf(),
      defaultSortOrder: 'descend',
      ellipsis: { showTitle: false },
      align: 'left',
      width: '17%',
      render: (text) => <EllipsisTooltip className='ellipsis'>{text}</EllipsisTooltip>,
    },
    {
      title: '',
      className: 'action',
      dataIndex: 'action',
      ellipsis: { showTitle: false },
      align: 'right',
      width: '8%',
    },
  ]

  return (
    <div className="page vehicle-list">
      <Row className="header">
        {/* Search Column */}
        <Col className="left-col">
          <Form form={form} layout="inline" onFinish={onFinish}>
            <InputText
              type="search"
              name="search"
              placeholder={intl.formatMessage({ id: 'vehicleTable.searchPlaceholder' })}
              initialValue={searchQuery}
              isRequired={false}
              onSubmit={(e) => {
                form.submit()
              }}
              wrapperClass="search-wrapper"
            />
          </Form>
        </Col>
        {/* Rest of the buttons */}
        <Col className="right-col">
          {HasPermission(PROMOTE_CHANGES) && (
            <LinkWithStatus
              href={`/manage-update-list`}
              title={intl.formatMessage({ id: 'vehicleTable.manageUpdates' })}
            />
          )}

          {(state?.permissions?.includes(TRACER_PREVIEW) ||
            state?.permissions?.includes(TRACER_PREVIEW_MGMT)) && (
            <Button
              onClick={() => {
                navigate('/tracer/preview?data_version=master')
              }}
            >
              {intl.formatMessage({ id: 'vehicleTable.launchTracerPreview' })}
            </Button>
          )}

          <LinkWithStatus
            href={`/vehicles/new`}
            title={intl.formatMessage({ id: 'vehicleTable.createNewVehicle' })}
            type="primary"
            testId="create-vehicle"
          />
        </Col>
      </Row>
      <Row className="body">
          <>
            <p className="table-title">{`${totalCount} Vehicles`}</p>

            <Row className="table-container">
              <Table
                dataSource={data}
                columns={columns}
                loading={!vehicles ? true : false}
                showSorterTooltip={false}
                pagination={
                  pagination ? {
                    current: currentPage,
                    total: totalCount,
                    pageSize: 10,
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    showTotal: (total, range) => {
                      // return `${range[0]}-${range[1]} of ${total} mappings`
                      return <RecordsCount count={total} />
                    },
                    onChange: (page) => {
                      // 10 is page size
                      const offset = (page - 1) * 10
                      const url = nextPageLink
                        ? new URL(nextPageLink)
                        : new URL(prevPageLink)
                      url.searchParams.set('offset', offset)
                      getPaginatedData(url.toString())
                    },
                  } : false
                }
                locale={{
                  emptyText: (
                    <div style={{ padding: '40px' }}>
                      <Empty description={ <RecordsCount count={0} /> } />
                    </div>
                  ),
                }}
              />
            </Row>
            {totalPages <= 1 && totalCount > 0 && (
              <Typography.Text>
                <RecordsCount count={totalCount} />
              </Typography.Text>
            )}
          </>
      </Row>
    </div>
  )
}

VehiclesTable.propTypes = {
  activityLogEnabled: PropTypes.bool,
  searchQuery: PropTypes.string,
  pagination: PropTypes.object,
  getPaginatedData: PropTypes.func,
}

export default VehiclesTable
